// ============================================================
// AutomaticSection.tsx — CASE CONTROL (nav label "Case control").
// Redesign (final sections pass): drawn in the case shell (SELF_FRAMED) on
// the shared SectionCards pattern —
//   • glance strip: status (Draft / Open / Closed), setup progress,
//     opened, handover;
//   • LIFECYCLE card: the Draft → Open → Closed track (the same rule the
//     case hero uses: Closed when closeDate is set, Open when
//     setupFinalised, else Draft) + the Finalise setup action;
//   • KEY DATES card (view / edit): Handover, Open date, Close date;
//   • CLAIM card (B2 — ClaimCard.tsx): claim type, route, track, band,
//     fixed recoverable costs, date of knowledge / death, and "Set
//     limitation date myself". Its OWN explicit-save card (a draft saved in
//     one request on Save), shown below the write-through cards.
// LOGIC UNCHANGED:
//   • finaliseSetup — the store action, shown only while !setupFinalised
//     (exactly as before: not gated on section completion here; the
//     Overview's copy of the button is the gated one);
//   • the three dates: DateInput onCommit (COMMIT-ON-BLUR, added 4 July —
//     the store only sees a value once focus leaves the widget, so a
//     mid-typing null never reaches it) → updateCase({ caseControl: {
//     ...caseControl, [field]: value || null } }) → debounced autosave;
//   • the three fields render UNCONDITIONALLY in edit (the old value-
//     truthy gate was a self-delete trap — reopening REQUIRES emptying
//     closeDate, which used to unmount the row mid-keystroke);
//   • non-sequential: no Mark complete (not in COMPLETABLE_SECTIONS).
//   • DATE HARDENING: a half-typed date never commits (DateInput); a close
//     date in the future or before the open date is refused on the spot
//     (utils/caseDateRules — the server 400s the same) and not saved; the
//     server's refusal of any of the three shows under its field.
// ============================================================
import React from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'
import { useSectionCompletion } from '../../store/useSectionCompletion'
import { getVisibleSequentialKeys } from '../../utils/sectionFlow'
import type { CaseControl } from '../../types/case.types'
import FormField from '../shared/FormField'
import DateInput from '../shared/DateInput'
import FieldError from '../shared/FieldError'
import { closeDateError, openDateError, serverDateErrorFor } from '../../utils/caseDateRules'
import { londonToday } from '../../utils/civilDate'
import { flushAutoSave } from '../../hooks/useAutoSave'
import { caseScope, useFieldRefusal } from '../../hooks/fieldRefusals'
import { broadcastFieldEdits } from '../../hooks/usePresence'
import { CARD, BTN_PRI } from '../clientInfo/ClientInfoSections'
import { FGRID } from '../clientInfo/clientInfoRegistry'
import SectionCards, { type CardDef } from '../case/sections/SectionCards'
import { KIcon, Glance, GlanceDash, Kv, Empty, Muted, DL, CHIP, CHIP_N } from '../case/sections/sectionKit'
import { fmtDay } from '../case/caseUi'
import ClaimCard from './ClaimCard'

type Stage = 'Draft' | 'Open' | 'Closed'
const DATE_FIELD = 'h-[38px] !rounded-[9px] !bg-casevo-input focus:!bg-casevo-surface border-casevo-border'
const STAGES: { key: Stage; title: string; sub: string }[] = [
  { key: 'Draft', title: 'Draft', sub: 'Setup in progress' },
  { key: 'Open', title: 'Open', sub: 'Setup finalised, case live' },
  { key: 'Closed', title: 'Closed', sub: 'A close date is set' },
]

const AutomaticSection: React.FC = () => {
  const { currentCase, updateCase, finaliseSetup, viewingPaneToggles, userRole } = useCasevoStore()
  const completedSections = useSectionCompletion((s) => s.completedSections)
  const saveError = useCasevoStore((s) => s.saveError)
  // A close date the rules refuse: shown, never saved.
  const [closeBlock, setCloseBlock] = React.useState<string | null>(null)
  const [openBlock, setOpenBlock] = React.useState<string | null>(null)
  // The server's refusals (the field went back to its saved value; the rest
  // of the batch saved) — shown under the field until it's edited again.
  const scope = currentCase ? caseScope(currentCase.id) : null
  const refusedHandover = useFieldRefusal(scope, 'handover')
  const refusedOpen = useFieldRefusal(scope, 'openDate')
  const refusedClose = useFieldRefusal(scope, 'closeDate')
  if (!currentCase) return null
  const { caseControl } = currentCase
  const { setupFinalised } = caseControl
  const stage: Stage = caseControl.closeDate ? 'Closed' : setupFinalised ? 'Open' : 'Draft'
  const stageIdx = STAGES.findIndex((s) => s.key === stage)
  const chain = getVisibleSequentialKeys(viewingPaneToggles, userRole)
  const done = chain.filter((k) => completedSections.has(k)).length

  // Same write as before — spread the LIVE caseControl so a remote edit
  // to a sibling field is never reverted.
  const handleChange = (field: keyof CaseControl, value: string): void => {
    const live = useCasevoStore.getState().currentCase?.caseControl ?? caseControl
    updateCase({ caseControl: { ...live, [field]: value || null } })
  }
  const updateMany = (changes: Partial<CaseControl>): void => {
    const live = useCasevoStore.getState().currentCase?.caseControl ?? caseControl
    updateCase({ caseControl: { ...live, ...changes } })
  }

  const dateRow = (label: string, v: string | null, hint: string, onAdd?: () => void): React.ReactNode => (
    <Kv label={label}>{v ? <span>{fmtDay(v)}</span> : onAdd ? <Empty onAdd={onAdd} /> : <Muted>{hint}</Muted>}</Kv>
  )
  const defs: CardDef<CaseControl>[] = [
    {
      key: 'dates', title: 'Key dates', sub: 'Handover, open and close dates', icon: <KIcon name="cal" size={17} />, full: true,
      fields: ['handover', 'openDate', 'closeDate'],
      hint: 'Dates save when you leave each field · Cancel undoes',
      view: (ctx) => (
        <dl className={`${DL} md:grid-cols-3`}>
          {dateRow('Handover', caseControl.handover, 'Not recorded', ctx.onAdd)}
          {dateRow('Open date', caseControl.openDate, 'Not recorded', ctx.onAdd)}
          {dateRow('Close date', caseControl.closeDate, 'Still open', undefined)}
        </dl>
      ),
      edit: () => (
        <div className={`${FGRID} md:grid-cols-3`}>
          {/* Unconditional render + commit-on-blur (see the header note). */}
          <FormField variant="info" label="Handover">
            {/* allowFuture: a handover can be planned ahead (the server allows a
                future handover). Open date stays past-only (a file is opened on
                or before today) — a future one says "Can't be in the future". */}
            <DateInput value={caseControl.handover} allowFuture onCommit={(v) => handleChange('handover', v || '')} ariaLabel="Handover" fieldClassName={DATE_FIELD} />
            <FieldError message={refusedHandover ?? serverDateErrorFor(saveError, 'handover') ?? undefined} />
          </FormField>
          <FormField variant="info" label="Open date">
            <DateInput value={caseControl.openDate} ariaLabel="Open date" fieldClassName={DATE_FIELD}
              onCommit={(v) => {
                const block = openDateError(v, useCasevoStore.getState().currentCase?.caseControl.closeDate ?? caseControl.closeDate)
                setOpenBlock(block)
                if (!block) handleChange('openDate', v || '')
              }} />
            <FieldError message={openBlock ?? refusedOpen ?? serverDateErrorFor(saveError, 'openDate') ?? undefined} />
          </FormField>
          <FormField variant="info" label="Close date">
            <DateInput value={caseControl.closeDate} ariaLabel="Close date" fieldClassName={DATE_FIELD}
              onCommit={(v) => {
                const block = closeDateError(v, useCasevoStore.getState().currentCase?.caseControl.openDate ?? caseControl.openDate, londonToday())
                setCloseBlock(block)
                if (!block) handleChange('closeDate', v || '')
              }} />
            <FieldError message={closeBlock ?? refusedClose ?? serverDateErrorFor(saveError, 'closeDate') ?? undefined} />
            <p className="text-[12px] text-casevo-text-soft mt-1">Setting a close date closes the case; clear it to reopen.</p>
          </FormField>
        </div>
      ),
    },
  ]

  // ── Lifecycle (the track + Finalise setup) ──
  const lifecycle = (
    <section aria-label="Lifecycle" className={`${CARD} mb-[14px] overflow-hidden`}>
      <div className="flex items-center gap-[11px] pt-[14px] pr-4 pb-3 pl-[18px]">
        <span className="w-8 h-8 rounded-[9px] bg-casevo-mint text-casevo-brand dark:text-[#a8e6c5] grid place-items-center shrink-0"><KIcon name="flag" size={17} /></span>
        <div className="min-w-0">
          <h3 className="m-0 text-[15px] font-semibold tracking-[-0.005em] leading-[1.2] text-casevo-forest">Lifecycle</h3>
          <div className="text-[12px] text-casevo-text-soft mt-px">Draft while setting up, Open once finalised, Closed when a close date is set</div>
        </div>
        <span className={`ml-auto ${stage === 'Open' ? CHIP : CHIP_N}`}>{stage}</span>
      </div>
      <ol className="grid grid-cols-3 gap-2 px-[18px] pb-4 m-0 list-none" aria-label="Case lifecycle">
        {STAGES.map((s, i) => {
          const cur = i === stageIdx
          const past = i < stageIdx
          return (
            <li key={s.key} aria-current={cur ? 'step' : undefined}
              className={`rounded-[11px] border px-3 py-[10px] min-w-0 ${cur
                ? 'border-casevo-brand bg-[color-mix(in_srgb,var(--casevo-mint)_55%,transparent)]'
                : 'border-casevo-border-soft bg-casevo-surface'}`}>
              <div className="flex items-center gap-2">
                <span className={`w-5 h-5 rounded-full grid place-items-center text-[11px] font-bold shrink-0 ${cur || past ? 'bg-casevo-primary text-white' : 'bg-casevo-border-soft text-casevo-text-soft'}`}>
                  {past ? <KIcon name="check" size={11} strokeWidth={3} /> : i + 1}
                </span>
                <b className={`text-[14px] ${cur ? 'text-casevo-forest' : past ? 'text-casevo-forest' : 'text-casevo-text-soft'}`}>{s.title}</b>
              </div>
              <div className="text-[12px] text-casevo-text-soft mt-1 truncate">{s.sub}</div>
            </li>
          )
        })}
      </ol>
      <div className="flex items-center gap-3 flex-wrap py-[11px] px-[18px] bg-casevo-input border-t border-casevo-border-soft">
        <span className="text-[12.5px] text-casevo-text-soft mr-auto">
          {setupFinalised
            ? 'Setup is finalised.'
            : `${done} of ${chain.length} setup sections complete. Finalising moves the case from Draft to Open.`}
        </span>
        {!setupFinalised && (
          <button type="button" className={BTN_PRI} onClick={finaliseSetup}>
            <KIcon name="check" size={15} />Finalise setup
          </button>
        )}
      </div>
    </section>
  )

  const glance = (
    <Glance cells={[
      { icon: 'flag', label: 'Status', value: stage, sub: stage === 'Draft' ? 'Setup not finalised' : stage === 'Open' ? 'Live case' : `Closed ${fmtDay(caseControl.closeDate)}` },
      { icon: 'check', label: 'Setup', value: <span className="tabular-nums">{done} of {chain.length}</span>, sub: setupFinalised ? 'Finalised' : 'Sections complete' },
      { icon: 'cal', label: 'Opened', value: caseControl.openDate ? fmtDay(caseControl.openDate) : <GlanceDash />, sub: 'Open date' },
      { icon: 'arrow', label: 'Handover', value: caseControl.handover ? fmtDay(caseControl.handover) : <GlanceDash />, sub: 'Handover date' },
    ]} />
  )

  return (
    <SectionCards<CaseControl>
      value={caseControl}
      onChange={updateMany}
      fieldKeyOf={() => undefined}
      onBroadcast={broadcastFieldEdits}
      onFlush={flushAutoSave}
      errors={[]}
      defs={defs}
      title="Case control"
      sub="The case's lifecycle, key dates and claim."
      glance={glance}
      notice={lifecycle}
      hideEditAll
      footer={<ClaimCard />}
      idPrefix={`cc-${currentCase.id}`}
    />
  )
}
export default AutomaticSection
